package com.hyperether.goodjob.scenes.components

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch

fun Modifier.noRippleClickable(onClick: () -> Unit): Modifier = composed {
    this.clickable(
        indication = null,
        interactionSource = remember { MutableInteractionSource() }) {
        onClick()
    }
}

@Composable
fun Modifier.verticalScrollbar(
    state: ScrollState,
    scrollbarWidth: androidx.compose.ui.unit.Dp = 12.dp,
    color: Color = Color.LightGray.copy(alpha = 1f)
): Modifier {
//    val alpha by animateFloatAsState(targetValue = if(state.isScrollInProgress) 1f else 0f,
//        animationSpec = tween(400, delayMillis = if(state.isScrollInProgress) 0 else 700)
//    )
    val coroutineScope = rememberCoroutineScope()
    return this then Modifier
        .pointerInput(state) {
            detectDragGestures(
                onDragStart = {},
                onDrag = { change, offset ->
                    val viewHeight = state.viewportSize.toFloat()
                    val contentHeight = state.maxValue + viewHeight

                    if (contentHeight > viewHeight) {
                        val scrollbarHeight = try {
                            (viewHeight * (viewHeight / contentHeight))
                                .coerceIn(10.dp.toPx()..viewHeight)
                        } catch (e: Exception) {
                            0f
                        }
                        val variableZone = viewHeight - scrollbarHeight

                        // Convert drag delta to scroll delta
                        val dragRatio = offset.y / variableZone
                        val scrollDelta = dragRatio * state.maxValue

                        coroutineScope.launch {
                            state.scrollBy(scrollDelta)
                        }
                    }
                }
            )
        }
        .drawWithContent {
            drawContent()


            val viewHeight = state.viewportSize.toFloat()
            val contentHeight = state.maxValue + viewHeight

            val scrollbarHeight = try {
                (viewHeight * (viewHeight / contentHeight)).coerceIn(10.dp.toPx()..viewHeight)
            } catch (e: Exception) {
                0f
            }
            val variableZone = viewHeight - scrollbarHeight
            val scrollbarYoffset = (state.value.toFloat() / state.maxValue) * variableZone

            drawRoundRect(
                cornerRadius = CornerRadius(scrollbarWidth.toPx() / 2, scrollbarWidth.toPx() / 2),
                color = color,
                topLeft = Offset(this.size.width - scrollbarWidth.toPx(), scrollbarYoffset),
                size = Size(scrollbarWidth.toPx(), scrollbarHeight)
            )
        }
}

@Composable
fun Modifier.verticalScrollbar(
    state: LazyListState,
    scrollbarWidth: Dp = 12.dp,
    color: Color = Color.LightGray.copy(alpha = 1f)
): Modifier {
    val coroutineScope = rememberCoroutineScope()

    return this then Modifier
        .pointerInput(state) {
            detectDragGestures(
                onDragStart = {},
                onDrag = { change, offset ->
                    val layoutInfo = state.layoutInfo
                    val viewHeight = layoutInfo.viewportSize.height.toFloat()
                    val averageItemHeight = layoutInfo.visibleItemsInfo.firstOrNull()?.size ?: 1
                    val totalContentHeight =
                        layoutInfo.totalItemsCount * averageItemHeight.toFloat()

                    if (totalContentHeight > viewHeight) {
                        val scrollbarHeight = try {
                            (viewHeight * (viewHeight / totalContentHeight))
                                .coerceIn(10.dp.toPx()..viewHeight)
                        } catch (e: Exception) {
                            0f
                        }
                        val variableZone = viewHeight - scrollbarHeight

                        // Convert drag delta to scroll delta
                        val dragRatio = offset.y / variableZone
                        val scrollDelta = dragRatio * (totalContentHeight - viewHeight)

                        coroutineScope.launch {
                            state.scrollBy(scrollDelta)
                        }
                    }
                }
            )
        }
        .drawWithContent {
            drawContent()

            val layoutInfo = state.layoutInfo
            if (layoutInfo.totalItemsCount == 0) return@drawWithContent

            val viewHeight = layoutInfo.viewportSize.height.toFloat()
            val averageItemHeight = layoutInfo.visibleItemsInfo.firstOrNull()?.size ?: 1
            val totalContentHeight = layoutInfo.totalItemsCount * averageItemHeight.toFloat()

            if (totalContentHeight > viewHeight) {
                val scrollbarHeight = try {
                    (viewHeight * (viewHeight / totalContentHeight))
                        .coerceIn(10.dp.toPx()..viewHeight)
                } catch (e: Exception) {
                    0f
                }
                val variableZone = viewHeight - scrollbarHeight

                // Calculate current scroll position
                val currentScrollOffset =
                    state.firstVisibleItemIndex * averageItemHeight.toFloat() +
                            state.firstVisibleItemScrollOffset
                val maxScrollValue = totalContentHeight - viewHeight
                val scrollbarYoffset = if (maxScrollValue > 0) {
                    (currentScrollOffset / maxScrollValue) * variableZone
                } else 0f

                drawRoundRect(
                    cornerRadius = CornerRadius(
                        scrollbarWidth.toPx() / 2,
                        scrollbarWidth.toPx() / 2
                    ),
                    color = color,
                    topLeft = Offset(this.size.width - scrollbarWidth.toPx(), scrollbarYoffset),
                    size = Size(scrollbarWidth.toPx(), scrollbarHeight)
                )
            }
        }
}